import React, {
  ChangeEvent,
  FC,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ButtonColor, ButtonSize, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { useFormik } from "formik";
import { fromJS, List, Map } from "immutable";
import { filter, isEqual } from "lodash";
import { isMobile } from "react-device-detect";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { ResetFilterButton } from "styles/searchFilterStyles";
import { StorageSearchValues } from "types/StorageForms/Search";
import Button from "../../components/Button/Button";
import Checkbox from "../../components/Checkbox/Checkbox";
import DropdownMenu from "../../components/DropdownMenu";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import Modal, { TextAreaSimple } from "../../components/Modal";
import { useDemoMode } from "../../contexts/DemoModeContext";
import useFetchOnceEffect from "../../hooks/useFetchOnceEffect";
import useProgressDelivery from "../../hooks/useProgressDelivery";
import injectReducer from "../../reduxFolder/injectReducer";
import { addParcels, getCart } from "../../reduxFolder/reducers/cart";
import { openRightPanel } from "../../reduxFolder/reducers/rightPanel";
import { getAddonsMethods } from "../../reduxFolder/reducers/services";
import storageReducer, {
  addParcelsToCart,
  bulkUpdateComment,
  deselectAll,
  getInventoryItems,
  getInventoryItemsPage,
  getShops,
  getStorage,
  selectAll,
  selectParcel,
  setActiveStorageBranch,
  updateStorage,
} from "../../reduxFolder/reducers/storage";
import {
  cartSelectCart,
  cartSelectIsEmpty,
  cartSelectLoading,
} from "../../reduxFolder/selectors/cartSelectors";
import {
  authSelectFfid,
  authSelectUnits,
} from "../../reduxFolder/selectors/globalSelectors";
import { panelSelectOpened } from "../../reduxFolder/selectors/rightPanelSelectors";
import { servicesSelectAddonsAll } from "../../reduxFolder/selectors/servicesSelectors";
import {
  storageSelectHasItems,
  storageSelectItems,
  storageSelectLoading,
  storageSelectParams,
  storageSelectSelected,
  storageSelectShops,
  storageSelectTotalAmount,
  storageSelectTotalWeight,
  storageSelectTransferringItems,
} from "../../reduxFolder/selectors/storageSelectors";
import {
  ListHeader,
  ListHeaderTitle,
  StorageButton,
} from "../../styles/parcelsStyles";
import { breakpoints } from "../../styles/styles";
import { OpenRightPanel } from "../../types/common/rightPanel";
import { toastResponseError } from "../../utils/responseMessageHelper";
import CartInfo from "./components/CartInfo/CartInfo";
import CurrentParcelGuide from "./components/CurrentParcelGuide/CurrentParcelGuide";
import DemoShipments from "./components/DemoShipments/DemoShipments";
import InventoryItems from "./components/InventoryItems/InventoryItems";
import NoItemsPlaceholder from "./components/NoItemsPlaceholder/NoItemsPlaceholder";
import CompactSearch from "./components/Search/CompactSearch";
import Search from "./components/Search/Search";
import { searchFormik } from "./components/Search/searchFormik";
import SearchPlaceholder from "./components/SearchPlaceholder/SearchPlaceholder";
import SelectedInfo from "./components/SelectedInfo/SelectedInfo";
import { isUnprocessedRequest } from "./components/ServiceRequests/requestHelper";
import StorageList from "./components/StorageList/StorageList";
import {
  ButtonLineWrapper,
  Buttons,
  ButtonsWrapper,
  DropdownMenuTriggerButton,
  GuideToggleButton,
  MenuIcon,
  SelectAllSwitchWrapper,
  StorageHeading,
  StyledGrid,
  TopLineWrapper,
  Wrapper,
} from "./Storage.styles";

interface StorageProps {
  units: string;
  isLoading: boolean;
  isCartLoading: boolean;
  isCartEmpty: boolean;
  storageHasItems: boolean;
  storageItems: List<any>;
  storageTransferringItems: List<any>;
  shops: any;
  addons: any;
  params: any;
  userFfid: string;
  totalAmount: number;
  totalWeight: number;
  getAddonsMethods: () => Promise<void>;
  addParcelsToCart: (data: any) => void;
  bulkUpdateComment: (data: {
    ids: number[];
    customer_comment: string;
  }) => Promise<any>;
  getCart: () => Promise<any>;
  selectAll: () => void;
  deselectAll: () => void;
  getStorage: (data?: any) => void;
  getInventoryItems: (data?: any) => void;
  getInventoryItemsPage: (group: string) => void;
  setActiveStorageBranch: (branchId: string | undefined) => void;
  getShops: () => Promise<void>;
  selectParcel: any;
  selectedParcels: any;
  openRightPanel: OpenRightPanel;
  cart: any;
  isPanelOpened: boolean;
}

const Storage: FC<StorageProps> = ({
  units,
  storageItems,
  storageTransferringItems,
  isLoading,
  isCartLoading,
  totalAmount,
  totalWeight,
  selectAll,
  deselectAll,
  addParcelsToCart,
  bulkUpdateComment,
  getStorage,
  getInventoryItems,
  getInventoryItemsPage,
  setActiveStorageBranch,
  getShops,
  selectParcel,
  selectedParcels,
  shops,
  storageHasItems,
  params,
  getCart,
  isCartEmpty,
  userFfid,
  getAddonsMethods,
  addons,
  openRightPanel,
  cart,
  isPanelOpened,
}) => {
  const { branchId } = useParams();
  const location = useLocation();
  const restoredLocationKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setActiveStorageBranch(branchId);
  }, [branchId]);

  const [isStorageList, setIsStorageList] = useState(true);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isCommentSaving, setIsCommentSaving] = useState(false);
  const navigate = useNavigate();
  const [handleResetCompactFunction, setHandleResetCompactFunction] =
    useState<Function>(() => null);
  const [, setAccess] = useProgressDelivery(userFfid);
  const { t } = useTranslation("common");
  const { isDemoEnabled } = useDemoMode();

  const storageHasTransferringItems = !!storageTransferringItems.size;

  // Always first show "transferring items" but only if there is no searchTerm.
  // If there is any filter applied then just show storage items.
  const extractedFilters = params.get("filter");
  const hasAnyFiltersApplied =
    filter(
      extractedFilters.toJS ? extractedFilters.toJS() : extractedFilters,
      (x) => x !== null && x !== undefined,
    ).length > 0;

  // If we are showing transferringItems then adjust both "totalAmount" and "totalWeight"
  const totalAmountWithTransferring = hasAnyFiltersApplied
    ? totalAmount
    : totalAmount + storageTransferringItems.size;
  // const totalWeightWithTransferring = hasAnyFiltersApplied
  //   ? totalWeight
  //   : totalWeight +
  //     storageTransferringItems.reduce(
  //       (sum: number, information: any) => sum + information.get("weight"),
  //       0,
  //     );

  // This also affects other booleans
  const hasAnyItems = hasAnyFiltersApplied
    ? storageHasItems
    : storageHasItems || storageHasTransferringItems;

  // The backend forbids mixing branches in a consolidation, so a non-empty
  // cart belongs to exactly one branch — the guide only concerns that tab.
  const cartBranchId = cart.getIn(["branch", "id"]);
  const isCartForActiveBranch =
    cartBranchId == null || String(cartBranchId) === branchId;

  // Manually opened guide is a transient view — leave it whenever the context
  // it was opened in changes.
  useEffect(() => {
    setIsGuideOpen(false);
  }, [branchId, isCartEmpty, isStorageList, hasAnyFiltersApplied]);

  const hasSelected = storageHasItems && !!selectedParcels.size;
  const activeItems = useMemo(
    () =>
      storageItems.filter((item: any) => {
        const requests = item.get("serviceRequestCount");
        const isProhibited = item.get("prohibited");

        if (isProhibited && isProhibited === true) return false;
        if (!requests.size) return true;
        return !requests.find((request: string) => request === "unprocessed");
      }),
    [storageItems],
  );

  const isAllSelected =
    !isLoading && hasSelected && activeItems.size === selectedParcels.size;

  useFetchOnceEffect(!isCartLoading, getCart);
  useFetchOnceEffect(!shops.size, getShops);
  useFetchOnceEffect(!addons.size, getAddonsMethods);
  useEffect(() => {
    const navigationState = location.state as
      | {
          restoreStorageParams?: any;
        }
      | undefined;
    const restoreStorageParams = navigationState?.restoreStorageParams;

    if (
      restoreStorageParams &&
      restoredLocationKeyRef.current !== location.key
    ) {
      restoredLocationKeyRef.current = location.key;
      const restoredParams = fromJS(restoreStorageParams)
        .set("page", 1)
        .set("isAddParcelsToCart", true);
      getStorage(restoredParams);
      return;
    }

    getStorage();
  }, [getStorage, branchId, location.key]);

  const onPagination = useCallback(
    () =>
      !isLoading &&
      getStorage(params.update("page", (page: number) => page + 1)),
    [getStorage, isLoading, params],
  );

  // Each category keeps its own page cursor in the store, so pagination only
  // needs to name the category that scrolled into view.
  const onItemsPagination = useCallback(
    (group: string) => !isLoading && getInventoryItemsPage(group),
    [getInventoryItemsPage, isLoading],
  );

  const selectedRangeParcels = useCallback(
    (endParcelId: string) => {
      const getIndexParcel = (parcelId: string) =>
        storageItems.findIndex((item: any) => item.get("id") === parcelId);
      const startSelected = getIndexParcel(selectedParcels.get(0));
      const endSelected = getIndexParcel(endParcelId);
      const from = Math.min(startSelected, endSelected);
      const to = Math.max(startSelected, endSelected);
      let selectedItems: any = List([]);

      for (let index = from; index <= to; index++) {
        const serviceRequestCount: any = storageItems.getIn([
          index,
          "serviceRequestCount",
        ]);
        const isProhibited = storageItems.getIn([index, "prohibited"]);
        if (
          !isUnprocessedRequest(serviceRequestCount) &&
          isProhibited !== true
        ) {
          selectedItems = selectedItems.push(storageItems.getIn([index, "id"]));
        }
      }

      return selectedItems;
    },
    [selectedParcels, storageItems],
  );

  const handleSelectAll = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.persist();
      selectAll();
    },
    [selectAll],
  );

  const handleDeselectAll = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.persist();
      deselectAll();
    },
    [deselectAll],
  );

  const handleToggleSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      if (isAllSelected === true) {
        deselectAll();
      } else {
        selectAll();
      }
    },
    [selectAll, deselectAll, isAllSelected],
  );

  const handleSend = useCallback(() => {
    if (!hasSelected && !isCartEmpty) {
      if (isMobile && window.innerWidth <= +breakpoints.xs) {
        openRightPanel(
          "NOT_FINISHED",
          Map({
            detailedItemID: cart.get("id"),
            parcel: cart,
          }),
        );
      } else {
        navigate("/shipping");
      }
      return;
    }

    addParcelsToCart({ id: selectedParcels, openedFromStorage: true });
    if (isCartEmpty) {
      setAccess("initialSteps");
    }
  }, [
    selectedParcels,
    hasSelected,
    isCartEmpty,
    addParcelsToCart,
    cart,
    openRightPanel,
  ]);

  const handleBulkTransfer = useCallback(() => {
    const ids = selectedParcels.toJS();
    openRightPanel(
      "SERVICE_REQUEST",
      Map({
        id: ids[0],
        state: "received",
        requestType: "requestTransferShipment",
        preventReLoading: true,
      }),
    );
  }, [openRightPanel, selectedParcels]);

  const noItems = !isLoading && !hasAnyItems;

  // The current-parcel guide replaces the storage list: automatically when
  // everything in storage went into the cart (noItems), or on demand via the
  // bottom-bar toggle (isGuideOpen).
  const showParcelGuide =
    isStorageList &&
    !isCartEmpty &&
    isCartForActiveBranch &&
    !hasAnyFiltersApplied &&
    (noItems || isGuideOpen);

  const handleGetStorageSubmit = (values: any) => {
    setIsStorageList(true);
    return getStorage(values);
  };

  const handleGetInventoryItemsSubmit = (values: any) => {
    setIsStorageList(false);
    return getInventoryItems(values);
  };

  const formOptions = useFormik<StorageSearchValues>(
    searchFormik(handleGetStorageSubmit, handleGetInventoryItemsSubmit),
  );

  const { values, initialValues, handleSubmit, handleReset } = formOptions;
  const isSearchDirty = !isLoading && !isEqual(values, initialValues);
  // Dirtiness minus the mode toggle itself: switching to "items" must not
  // count as a filter, or the demo items would never show on an empty search.
  const hasItemsFiltersApplied =
    !isLoading &&
    !isEqual(
      { ...values, searchMode: initialValues.searchMode },
      initialValues,
    );

  const handleResetClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      handleReset(e);
      handleSubmit();
    },
    [handleReset, handleSubmit],
  );

  const handleResetCompactClick = () => {
    if (!handleResetCompactFunction) return;
    handleResetCompactFunction();
  };

  const handleSetResetCompactFunction = (func: Function) =>
    setHandleResetCompactFunction(func);

  return (
    <Wrapper $isPanelOpened={isPanelOpened}>
      <StyledGrid fluid>
        <LoadingBar isLoading={isLoading} isCartLoading={isCartLoading} />
        <StorageHeading>
          {hasAnyItems && isStorageList && !showParcelGuide && (
            <ListHeader>
              <Visible xs>
                <SelectAllSwitchWrapper>
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleToggleSelectAll}
                  />
                </SelectAllSwitchWrapper>
              </Visible>
              <ListHeaderTitle>
                {t("parcels.total", { amount: totalAmountWithTransferring })} /{" "}
                {`${totalWeight.toFixed(2)} ${t(`units.${units}.primaryShort`)}`}
              </ListHeaderTitle>
              <Hidden xs>
                {hasSelected ? (
                  <StorageButton onClick={handleDeselectAll}>
                    {t("common.deselect")}
                  </StorageButton>
                ) : (
                  <StorageButton onClick={handleSelectAll}>
                    {t("common.selectAll")}
                  </StorageButton>
                )}
                {hasAnyFiltersApplied && (
                  <ResetFilterButton onClick={handleResetClick}>
                    {t("parcels.search.resetFilter")}
                  </ResetFilterButton>
                )}
              </Hidden>
              <Visible xs>
                {hasAnyFiltersApplied && (
                  <ResetFilterButton onClick={handleResetCompactClick}>
                    {t("parcels.search.resetFilter")}
                  </ResetFilterButton>
                )}
              </Visible>
            </ListHeader>
          )}
          {(hasAnyItems || noItems || !isStorageList) && (
            <>
              <Hidden xs>
                <Search formOptions={formOptions} />
              </Hidden>

              <Visible xs>
                <CompactSearch
                  formOptions={formOptions}
                  handleSetResetCompactFunction={handleSetResetCompactFunction}
                />
              </Visible>
            </>
          )}
        </StorageHeading>

        {hasAnyItems && isStorageList && !showParcelGuide && (
          <StorageList
            addons={addons}
            units={units}
            storageItems={storageItems}
            storageTransferringItems={storageTransferringItems}
            hasAnyFiltersApplied={hasAnyFiltersApplied}
            totalAmount={totalAmount}
            selectedParcels={selectedParcels}
            handleSelect={selectParcel}
            onPagination={onPagination}
            selectedRangeParcels={selectedRangeParcels}
            disabled={isCartLoading}
          />
        )}
        {!isStorageList && (
          <InventoryItems
            // @ts-ignore
            handleReset={handleResetClick}
            handleResetCompact={handleResetCompactClick}
            isSearchDirty={isSearchDirty}
            hasAnyFiltersApplied={hasItemsFiltersApplied}
            onPagination={onItemsPagination}
          />
        )}
        {isStorageList &&
          noItems &&
          !hasAnyFiltersApplied &&
          (isCartEmpty || !isCartForActiveBranch) &&
          !isCartLoading &&
          (isDemoEnabled ? <DemoShipments /> : <NoItemsPlaceholder />)}
        {showParcelGuide && (
          <CurrentParcelGuide
            onClose={noItems ? undefined : () => setIsGuideOpen(false)}
          />
        )}
        {isStorageList && (
          <>
            <Hidden xs>
              {noItems && hasAnyFiltersApplied && (
                <SearchPlaceholder handleReset={handleResetClick} />
              )}
            </Hidden>
            <Visible xs>
              {noItems && hasAnyFiltersApplied && (
                <SearchPlaceholder handleReset={handleResetCompactClick} />
              )}
            </Visible>
          </>
        )}
        {isStorageList && (hasSelected || !isCartEmpty) && (
          <ButtonsWrapper>
            {!hasSelected ? (
              <Buttons $singleRow>
                <CartInfo />
                <Button
                  variant={ButtonVariant.Filled}
                  color={ButtonColor.Primary}
                  size={ButtonSize.Large}
                  disabled={isCartLoading}
                  onClick={handleSend}
                >
                  {t("common.continue")}
                </Button>
                {hasAnyItems && isCartForActiveBranch && !showParcelGuide && (
                  <Hidden xs>
                    <GuideToggleButton
                      $isPanelOpened={isPanelOpened}
                      onClick={() => setIsGuideOpen(true)}
                    >
                      {t("parcels.currentParcelGuide.toggle")}
                    </GuideToggleButton>
                  </Hidden>
                )}
              </Buttons>
            ) : (
              <Buttons>
                <TopLineWrapper>
                  <SelectedInfo />
                  <Visible xs>
                    <StorageButton
                      disabled={isCartLoading}
                      onClick={handleDeselectAll}
                    >
                      {t("common.deselect")}
                    </StorageButton>
                  </Visible>
                </TopLineWrapper>
                <ButtonLineWrapper>
                  <Button
                    variant={ButtonVariant.Filled}
                    color={ButtonColor.Primary}
                    size={ButtonSize.Large}
                    disabled={isCartLoading}
                    onClick={handleSend}
                  >
                    {t("common.addToCart")}
                  </Button>
                  <DropdownMenu
                    disabled={isCartLoading}
                    place="above"
                    trigger={
                      <DropdownMenuTriggerButton
                        variant={ButtonVariant.Bordered}
                        color={ButtonColor.Neutral}
                        size={ButtonSize.Large}
                        disabled={isCartLoading}
                      >
                        {t("common.options")}
                        <MenuIcon type={IconType.Menu} />
                      </DropdownMenuTriggerButton>
                    }
                  >
                    <DropdownMenu.Item onClick={handleBulkTransfer}>
                      {t("serviceRequests.requestTransferShipment.title")}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={() => setIsCommentModalOpen(true)}
                    >
                      {t("common.assignComment")}
                    </DropdownMenu.Item>
                  </DropdownMenu>
                </ButtonLineWrapper>
                <Hidden xs>
                  <StorageButton
                    disabled={isCartLoading}
                    onClick={handleDeselectAll}
                  >
                    {t("common.deselect")}
                  </StorageButton>
                </Hidden>
              </Buttons>
            )}
          </ButtonsWrapper>
        )}
      </StyledGrid>
      <Modal
        isOpen={isCommentModalOpen}
        onClose={() => {
          setIsCommentModalOpen(false);
          setCommentText("");
        }}
        closeDisabled={isCommentSaving}
        title={t("common.assignComment")}
        footer={
          <>
            <Button
              disabled={isCommentSaving}
              onClick={() => {
                setIsCommentModalOpen(false);
                setCommentText("");
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant={ButtonVariant.Filled}
              color={ButtonColor.Primary}
              disabled={isCommentSaving}
              isLoading={isCommentSaving}
              onClick={() => {
                setIsCommentSaving(true);
                bulkUpdateComment({
                  ids: selectedParcels.toJS(),
                  customer_comment: commentText,
                })
                  .then(() => {
                    setIsCommentModalOpen(false);
                    setCommentText("");
                  })
                  .catch(toastResponseError)
                  .finally(() => setIsCommentSaving(false));
              }}
            >
              {t("common.save")}
            </Button>
          </>
        }
      >
        <TextAreaSimple
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
        />
      </Modal>
    </Wrapper>
  );
};

const withConnect = connect(
  createStructuredSelector({
    userFfid: authSelectFfid,
    storageItems: storageSelectItems,
    storageTransferringItems: storageSelectTransferringItems,
    totalAmount: storageSelectTotalAmount,
    totalWeight: storageSelectTotalWeight,
    storageHasItems: storageSelectHasItems,
    isLoading: storageSelectLoading,
    isCartLoading: cartSelectLoading,
    selectedParcels: storageSelectSelected,
    params: storageSelectParams,
    shops: storageSelectShops,
    units: authSelectUnits,
    isCartEmpty: cartSelectIsEmpty,
    addons: servicesSelectAddonsAll,
    cart: cartSelectCart,
    isPanelOpened: panelSelectOpened,
  }),
  {
    addParcelsToCart,
    bulkUpdateComment,
    getCart,
    getStorage,
    getInventoryItems,
    getInventoryItemsPage,
    setActiveStorageBranch,
    selectAll,
    deselectAll,
    selectParcel,
    updateStorage,
    addParcels,
    getShops,
    getAddonsMethods,
    openRightPanel,
  },
);

const withStorageReducer = injectReducer({
  key: "storage",
  reducer: storageReducer,
});

const enhance = compose(withStorageReducer, withConnect, memo);

export default enhance(Storage);
